import { createContext, useContext, useState, useCallback } from 'react';
import type { Toast as ToastType, ToastVariant } from '../types';

// Toast context
interface ToastContextType {
  toasts: ToastType[];
  addToast: (message: string, variant?: ToastVariant, duration?: number) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastType[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (message: string, variant: ToastVariant = 'info', duration: number = 3000) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const newToast: ToastType = { id, message, variant, duration };
      setToasts((prev) => [...prev, newToast]);
      if (duration > 0) {
        setTimeout(() => removeToast(id), duration);
      }
    },
    [removeToast]
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

const variantStyles: Record<ToastVariant, { bg: string; icon: string; iconColor: string }> = {
  success: { bg: 'bg-ink border-ink', icon: 'check_circle', iconColor: 'text-green-400' },
  error: { bg: 'bg-ink border-ink', icon: 'error', iconColor: 'text-red-400' },
  warning: { bg: 'bg-ink border-ink', icon: 'warning', iconColor: 'text-primary' },
  info: { bg: 'bg-ink border-ink', icon: 'info', iconColor: 'text-accent-muted' },
};

function ToastItem({ toast, onDismiss }: { toast: ToastType; onDismiss: (id: string) => void }) {
  const [isExiting, setIsExiting] = useState(false);
  const style = variantStyles[toast.variant];

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 200);
  };

  return (
    <div
      role="alert"
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg transition-all duration-200 ${style.bg} ${
        isExiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'
      }`}
    >
      <span className={`material-icons-round ${style.iconColor}`}>{style.icon}</span>
      <p className="text-white text-sm flex-1">{toast.message}</p>
      <button
        onClick={handleDismiss}
        className="p-1 hover:bg-white/10 rounded transition-colors"
        aria-label="Dismiss"
      >
        <span className="material-icons-round text-ink-tertiary text-lg">close</span>
      </button>
    </div>
  );
}

function ToastContainer({
  toasts,
  onDismiss,
}: {
  toasts: ToastType[];
  onDismiss: (id: string) => void;
}) {
  if (toasts.length === 0) return null;
  return (
    <div
      className="fixed bottom-20 right-4 z-[100] flex flex-col gap-2 max-w-sm"
      aria-live="polite"
      aria-atomic="true"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

export default ToastProvider;
